import React, { useState, useEffect } from "react";
import config from "../config";
import { shippingLabel } from "../constants/shippingPlans";

const Orders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${config.BASE_URL}/orders`);
      const data = await res.json();
      if (data.success) {
        setOrders(data.data.orders || []);
      } else {
        alert(data.message || "Failed to fetch orders");
      }
    } catch (err) {
      console.error(err);
      alert("Error fetching orders");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleString();
  };

  return (
    <div className="p-6 bg-[#211436] min-h-screen font-sans text-white">
      {loading && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white px-6 py-4 rounded-lg shadow-lg text-gray-800">
            Loading orders...
          </div>
        </div>
      )}

      <div className="mb-4 bg-[#351F57] rounded-[6px] px-4 py-3">
        <p className="text-sm text-gray-300">
          Customer orders with products, quantities, and delivery details. Shipping
          charged once per order (highest tier in cart).
        </p>
      </div>

      <div className="overflow-x-auto p-3 bg-[#351F57] rounded-[6px]">
        <table className="w-full text-left text-sm text-white table-fixed border border-gray-200">
          <thead className="bg-[#482772] border-b border-gray-300">
            <tr className="divide-x divide-gray-300">
              <th className="p-3">Order #</th>
              <th className="p-3">Customer</th>
              <th className="p-3 text-center">Total</th>
              <th className="p-3 text-center">Status</th>
              <th className="p-3 text-center">Date</th>
              <th className="p-3 text-center">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {orders.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-4 text-center text-gray-400">
                  No orders yet.
                </td>
              </tr>
            ) : (
              orders.map((order, index) => (
                <React.Fragment key={order.id}>
                  <tr
                    className={
                      index % 2 === 0 ? "bg-[#482772]" : "bg-[#351F57]"
                    }
                  >
                    <td className="p-3 font-semibold">#{order.id}</td>
                    <td className="p-3">
                      <div>{order.customerName}</div>
                      <div className="text-xs text-gray-300">{order.email}</div>
                    </td>
                    <td className="p-3 text-center font-medium">
                      ${Number(order.total).toFixed(2)}
                    </td>
                    <td className="p-3 text-center">
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          order.status === "paid"
                            ? "bg-green-600"
                            : "bg-yellow-600"
                        }`}
                      >
                        {order.status}
                      </span>
                    </td>
                    <td className="p-3 text-center text-xs">
                      {formatDate(order.createdAt)}
                    </td>
                    <td className="p-3 text-center">
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedId(
                            expandedId === order.id ? null : order.id
                          )
                        }
                        className="px-3 py-1 text-sm bg-[#5832AD] rounded-md hover:bg-purple-700"
                      >
                        {expandedId === order.id ? "Hide" : "View"}
                      </button>
                    </td>
                  </tr>
                  {expandedId === order.id && (
                    <tr className="bg-[#2b145c]">
                      <td colSpan={6} className="p-4">
                        <div className="grid md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <h4 className="font-bold mb-2">Delivery</h4>
                            <p>Phone: {order.phone}</p>
                            <p>Address: {order.address}</p>
                            <p>Country: {order.country}</p>
                            <p>Zip: {order.zipCode || "—"}</p>
                            <p className="mt-2">
                              Subtotal: ${Number(order.subtotal).toFixed(2)} ·
                              Shipping: $
                              {Number(order.shippingFee).toFixed(2)}
                            </p>
                          </div>
                          <div>
                            <h4 className="font-bold mb-2">Items</h4>
                            <ul className="space-y-2">
                              {(order.items || []).map((item) => (
                                <li
                                  key={item.id}
                                  className="border-b border-white/10 pb-2"
                                >
                                  {item.productTitle} × {item.quantity} — $
                                  {Number(item.lineTotal).toFixed(2)}
                                  <span className="block text-xs text-gray-400">
                                    {shippingLabel(item.shippingType)}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Orders;
